import React, { useState, useEffect } from 'react';
import {
    Typography,
    Checkbox,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
    ListItemText,
    Chip,
} from '@mui/material';
import { AddButton } from '../button/button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { STATUS } from '../../utils/status';
import { useParams } from 'react-router-dom';
import { updateCompletedTask } from '../../redux/slices/upload';
import { setMultiSelectEdited } from '../../redux/reducers/isEditTaskCompleted';

const OTHER_SENTINEL = '___other___';

interface MultiSelectActionProps {
    actionValue: any[];
    actionIndex: number;
    action: string;
    result: any;
    projectStatus: string;
    heading?: string;
}

export default function MultiSelectAction({
    action,
    actionValue,
    actionIndex,
    result,
    projectStatus,
    heading
}: MultiSelectActionProps) {
    const isProjectStatusCompleted = projectStatus === 'COMPLETED';
    const [selectedOptions, setSelectedOptions] = useState(actionValue);

    // Normalize initial selected values: replace is_other items with the sentinel
    const initSelectedValues = (): string[] => {
        return actionValue[0]?.selected.map((item: any) =>
            item.is_other ? OTHER_SENTINEL : item.value
        ) || [];
    };

    // Get initial other text from the is_other item if present
    const initOtherText = (): string => {
        const otherItem = actionValue[0]?.selected.find((item: any) => item.is_other);
        return otherItem ? otherItem.value : '';
    };

    const [selectedValues, setSelectedValues] = useState<string[]>(initSelectedValues);
    const [otherText, setOtherText] = useState<string>(initOtherText);
    const [isEdited, setIsEdited] = useState(false);
    const { id } = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const { updateCompletedTaskStatus } = useSelector((state: RootState) => state.upload);
    const fetchPermissions = useSelector((state: RootState) => state.projects.permissions);

    useEffect(() => {
        setSelectedValues(initSelectedValues());
        setOtherText(initOtherText());
    }, [actionValue]);

    const isOtherSelected = selectedValues.includes(OTHER_SENTINEL);

    const handleOptionChange = (event: any) => {
        const newSelectedValues: string[] = event.target.value;
        setSelectedValues(newSelectedValues);
        setIsEdited(true);
        dispatch(setMultiSelectEdited(true));
    };

    const handleOtherTextChange = (text: string) => {
        setOtherText(text);
        setIsEdited(true);
        dispatch(setMultiSelectEdited(true));
    };

    const buildSelectedPayload = () => {
        return selectedValues.map(val => {
            if (val === OTHER_SENTINEL) {
                return { value: otherText, is_other: true, output_value: otherText };
            }
            const configOption = selectedOptions[0]?.config_details?.value?.value?.find(
                (opt: any) => opt.value === val
            );
            return {
                value: val,
                output_value: configOption?.output_value ?? val,
                is_other: false,
            };
        });
    };

    const handleSave = () => {
        if (isProjectStatusCompleted) return;

        const payload = {
            project_id: id,
            task_id: result.tasks[0].task_id,
            allocation_id: result.tasks[0].task_allocation_id,
            response_id: result.tasks[0].responses[0].id,
            action_id: actionIndex,
            action: action,
            action_value: {
                id: selectedOptions[0].id,
                selected: buildSelectedPayload(),
            },
        };
        dispatch(updateCompletedTask(payload));
        setIsEdited(false);
        dispatch(setMultiSelectEdited(false));
    };

    console.log({ selectedValues, selectedOptions });

    return (
        <>
            <FormControl fullWidth>
                <Typography sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                    {heading} :
                </Typography>
                <Select
                    multiple
                    style={{
                        maxHeight: 300,
                        maxWidth: 850,
                    }}
                    value={selectedValues}
                    renderValue={(selected) => (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '4px',
                        }}>
                            {selected.map((value) => (
                                <Chip
                                    key={value}
                                    label={value === OTHER_SENTINEL ? (otherText || 'Other') : value}
                                    size="small"
                                />
                            ))}
                        </div>
                    )}
                    disabled={fetchPermissions?.completing_task_action_details?.[1] === "read"}
                    onChange={handleOptionChange}
                    fullWidth
                >
                    {selectedOptions?.flatMap(option =>
                        option.config_details?.value?.value?.map((opt: any) => (
                            <MenuItem key={opt.index} value={opt.value}>
                                <Checkbox checked={selectedValues.includes(opt.value)} />
                                <ListItemText primary={opt.value} />
                            </MenuItem>
                        ))
                    )}
                    <MenuItem value={OTHER_SENTINEL}>
                        <Checkbox checked={isOtherSelected} />
                        <ListItemText primary="Other" />
                    </MenuItem>
                </Select>

            </FormControl>

            {!isProjectStatusCompleted && isEdited && (
                fetchPermissions?.completing_task_action_details?.[1] !== "read" && (
                    <AddButton
                        startIcon={actionIndex === selectedOptions[0]?.id && updateCompletedTaskStatus === STATUS.PENDING ? (
                            <CircularProgress />
                        ) : null}
                        disabled={actionIndex === selectedOptions[0]?.id && updateCompletedTaskStatus === STATUS.PENDING}
                        onClick={handleSave}
                    >
                        Save
                    </AddButton>
                )
            )}
        </>
    );
}
